import type { DOSAGE_CALCULATION_CALCULATOR_STRINGS } from "@/components/tools/dosage-calculation-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  convertUnit,
  desiredOverHave,
  roundHalfUp,
  unitInfo,
  weightBased,
} from "@/lib/tool-kit/stats-clinical/dosage";

/*
 * Turns the reader's inputs into result cards and worked steps for the dosage-calculation
 * practice calculator. Plain functions, one per mode.
 */

type Strings = typeof DOSAGE_CALCULATION_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export type DosageMode = "desiredHave" | "weight" | "convert";
export type SupplyForm = "tablet" | "capsule" | "ml";

export interface DosageOutput {
  cards: [string, string][];
  steps: string[];
}

export interface DosageContext {
  strings: Strings;
  fmt: Format;
  locale: string;
  decimals: number;
}

function num(ctx: DosageContext, value: number, max = 3): string {
  return ctx.fmt(value, { maximumFractionDigits: max });
}

function unitLabel(strings: Strings, symbol: string): string {
  if (symbol === "units") {
    return strings.unitUnits;
  }
  return symbol === "mEq" ? strings.unitMeq : symbol;
}

export function formLabel(
  strings: Strings,
  form: SupplyForm,
  locale: string,
  amount: number
): string {
  if (form === "ml") {
    return "mL";
  }
  const key = form === "tablet" ? strings.formTablet : strings.formCapsule;
  return tPlural(key, locale, amount, { count: "" }).trim();
}

/** Put a dose into the unit of the supply: null when the two cannot be compared. */
export function matchUnits(
  value: number,
  from: string,
  to: string
): { value: number; factor: number | null } | null {
  if (from === to) {
    return { value, factor: null };
  }
  const converted = convertUnit(value, from, to);
  return converted
    ? { value: converted.value, factor: converted.factor }
    : null;
}

export interface DesiredHaveInputs {
  desired: number;
  desiredUnit: string;
  have: number;
  haveUnit: string;
  quantity: number;
  form: SupplyForm;
}

export function computeDesiredHave(
  inputs: DesiredHaveInputs,
  ctx: DosageContext
): DosageOutput | null {
  const { strings, locale, decimals } = ctx;
  const matched = matchUnits(
    inputs.desired,
    inputs.desiredUnit,
    inputs.haveUnit
  );
  if (!matched) {
    return null;
  }
  const raw = desiredOverHave(matched.value, inputs.have, inputs.quantity);
  const rounded = roundHalfUp(raw, decimals);
  const unit = unitLabel(strings, inputs.haveUnit);
  const form = formLabel(strings, inputs.form, locale, rounded);
  const steps: string[] = [];
  if (matched.factor !== null) {
    steps.push(
      t(strings.stepConvert, {
        value: num(ctx, inputs.desired),
        from: unitLabel(strings, inputs.desiredUnit),
        factor: num(ctx, matched.factor, 6),
        to: unit,
        result: num(ctx, matched.value, 6),
      })
    );
  }
  steps.push(
    t(strings.stepDesiredHave, {
      desired: num(ctx, matched.value, 6),
      have: num(ctx, inputs.have),
      unit,
      quantity: num(ctx, inputs.quantity),
      form:
        inputs.form === "ml"
          ? "mL"
          : formLabel(strings, inputs.form, locale, inputs.quantity),
      raw: num(ctx, raw, 4),
      answerForm: form,
    })
  );
  return {
    cards: [[strings.cardAmount, `${num(ctx, rounded, decimals)} ${form}`]],
    steps,
  };
}

export interface WeightInputs {
  perKg: number;
  doseUnit: string;
  weight: number;
  weightUnit: "kg" | "lb";
  basis: "dose" | "day";
  dosesPerDay: number;
  supply: { have: number; haveUnit: string; quantity: number } | null;
}

function weightSteps(
  inputs: WeightInputs,
  ctx: DosageContext,
  result: ReturnType<typeof weightBased>
): string[] {
  const { strings } = ctx;
  const unit = unitLabel(strings, inputs.doseUnit);
  const steps: string[] = [];
  if (inputs.weightUnit === "lb") {
    steps.push(
      t(strings.stepWeight, {
        pounds: num(ctx, inputs.weight),
        kilograms: num(ctx, result.kilograms),
      })
    );
  }
  const total = inputs.perKg * result.kilograms;
  steps.push(
    t(strings.stepDosePerKg, {
      perKg: num(ctx, inputs.perKg),
      unit,
      kilograms: num(ctx, result.kilograms),
      total: num(ctx, total, 4),
    })
  );
  if (result.perDay !== null) {
    steps.push(
      t(strings.stepSplit, {
        total: num(ctx, result.perDay, 4),
        unit,
        doses: num(ctx, inputs.dosesPerDay),
        perDose: num(ctx, result.perDose, 4),
      })
    );
  }
  return steps;
}

export function computeWeight(
  inputs: WeightInputs,
  ctx: DosageContext
): DosageOutput | null {
  const { strings, decimals } = ctx;
  const result = weightBased(
    inputs.perKg,
    inputs.weight,
    inputs.weightUnit,
    inputs.basis,
    inputs.dosesPerDay
  );
  const unit = unitLabel(strings, inputs.doseUnit);
  const cards: [string, string][] = [];
  if (inputs.weightUnit === "lb") {
    cards.push([strings.cardWeight, `${num(ctx, result.kilograms)} kg`]);
  }
  if (result.perDay !== null) {
    cards.push([
      strings.cardPerDay,
      `${num(ctx, roundHalfUp(result.perDay, decimals), decimals)} ${unit}`,
    ]);
  }
  cards.push([
    strings.cardPerDose,
    `${num(ctx, roundHalfUp(result.perDose, decimals), decimals)} ${unit}`,
  ]);
  const steps = weightSteps(inputs, ctx, result);
  if (inputs.supply) {
    const matched = matchUnits(
      result.perDose,
      inputs.doseUnit,
      inputs.supply.haveUnit
    );
    if (!matched) {
      return null;
    }
    const volume = desiredOverHave(
      matched.value,
      inputs.supply.have,
      inputs.supply.quantity
    );
    cards.push([
      strings.cardVolume,
      `${num(ctx, roundHalfUp(volume, decimals), decimals)} mL`,
    ]);
    steps.push(
      t(strings.stepVolume, {
        dose: num(ctx, matched.value, 4),
        unit: unitLabel(strings, inputs.supply.haveUnit),
        have: num(ctx, inputs.supply.have),
        quantity: num(ctx, inputs.supply.quantity),
        volume: num(ctx, volume, 4),
      })
    );
  }
  return { cards, steps };
}

export function computeConversion(
  value: number,
  from: string,
  to: string,
  ctx: DosageContext
): DosageOutput | null {
  const converted = convertUnit(value, from, to);
  if (!(converted && unitInfo(from))) {
    return null;
  }
  return {
    cards: [
      [ctx.strings.cardConverted, `${num(ctx, converted.value, 6)} ${to}`],
    ],
    steps: [
      t(ctx.strings.stepConversion, {
        value: num(ctx, value),
        from,
        factor: num(ctx, converted.factor, 6),
        to,
        result: num(ctx, converted.value, 6),
      }),
    ],
  };
}

export interface DosageFields {
  rounding: string;
  desired: string;
  desiredUnit: string;
  have: string;
  haveUnit: string;
  quantity: string;
  form: SupplyForm;
  perKg: string;
  doseUnit: string;
  basis: "dose" | "day";
  doses: string;
  weight: string;
  weightUnit: "kg" | "lb";
  supplyHave: string;
  supplyUnit: string;
  supplyQuantity: string;
  convertValue: string;
  convertFrom: string;
  convertTo: string;
}

export const INITIAL_FIELDS: DosageFields = {
  rounding: "2",
  desired: "",
  desiredUnit: "mg",
  have: "",
  haveUnit: "mg",
  quantity: "1",
  form: "tablet",
  perKg: "",
  doseUnit: "mg",
  basis: "dose",
  doses: "2",
  weight: "",
  weightUnit: "kg",
  supplyHave: "",
  supplyUnit: "mg",
  supplyQuantity: "5",
  convertValue: "",
  convertFrom: "kg",
  convertTo: "g",
};

function positive(text: string): number | null {
  const value = parseNumberInput(text);
  return value !== null && value > 0 ? value : null;
}

function weightFromFields(
  f: DosageFields,
  ctx: DosageContext
): DosageOutput | null {
  const perKg = positive(f.perKg);
  const weight = positive(f.weight);
  const doses = f.basis === "day" ? positive(f.doses) : 1;
  if (perKg === null || weight === null || doses === null) {
    return null;
  }
  const supplyHave = positive(f.supplyHave);
  const supplyQuantity = positive(f.supplyQuantity);
  const supply =
    supplyHave !== null && supplyQuantity !== null
      ? { have: supplyHave, haveUnit: f.supplyUnit, quantity: supplyQuantity }
      : null;
  return computeWeight(
    {
      perKg,
      doseUnit: f.doseUnit,
      weight,
      weightUnit: f.weightUnit,
      basis: f.basis,
      dosesPerDay: doses,
      supply,
    },
    ctx
  );
}

/** The output for the chosen mode, or null while the inputs are incomplete or incompatible. */
export function computeFromFields(
  mode: DosageMode,
  f: DosageFields,
  ctx: DosageContext
): DosageOutput | null {
  if (mode === "desiredHave") {
    const desired = positive(f.desired);
    const have = positive(f.have);
    const quantity = positive(f.quantity);
    if (desired === null || have === null || quantity === null) {
      return null;
    }
    return computeDesiredHave(
      {
        desired,
        desiredUnit: f.desiredUnit,
        have,
        haveUnit: f.haveUnit,
        quantity,
        form: f.form,
      },
      ctx
    );
  }
  if (mode === "weight") {
    return weightFromFields(f, ctx);
  }
  const value = positive(f.convertValue);
  return value === null
    ? null
    : computeConversion(value, f.convertFrom, f.convertTo, ctx);
}
